import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, Gift, Sparkles, Star, Crown, Users, Clock, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useApp } from '../App';
import { getRaffleHistory } from '../lib/api';

// Estilos para las animaciones del cofre
const chestStyles = `
@keyframes chest-float {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-12px); }
}

@keyframes chest-glow-pulse {
  0%, 100% { opacity: 0.4; }
  50% { opacity: 0.7; }
}

.chest-premium {
  animation: chest-float 4s ease-in-out infinite;
  filter: drop-shadow(0 8px 24px rgba(255, 198, 64, 0.4));
  transform-origin: center bottom;
}

.chest-premium:hover {
  filter: drop-shadow(0 12px 40px rgba(255, 198, 64, 0.7)) !important;
  animation-play-state: paused;
}

.chest-glow-bg {
  animation: chest-glow-pulse 3s ease-in-out infinite;
}
`;

const RaffleCard = ({ raffle, onSelect }) => {
  const isFree = raffle.template_type === 'free';
  const is100 = raffle.template_type === 'mxn100';
  const isFeatured = raffle.featured;
  const percentage = raffle.percentage || 0;
  const sold = raffle.sold || 0;
  const total = raffle.total_chests || 5000;
  const price = isFree ? 'GRATIS' : is100 ? '$100' : '$50';
  const prizeAmount = isFree ? 8000 : (raffle.prize_main_amount || 30000);

  return (
    <>
      <style>{chestStyles}</style>
      
      <div
        className={`relative rounded-3xl overflow-visible transition-all duration-300 hover:-translate-y-2 cursor-pointer group
          ${isFeatured
            ? 'bg-gradient-to-b from-[rgba(255,198,64,0.08)] via-transparent to-[rgba(9,10,13,0.95)] border-2 border-[rgba(255,198,64,0.4)] shadow-[0_0_40px_rgba(255,198,64,0.15)]'
            : 'bg-gradient-to-b from-transparent via-transparent to-[rgba(9,10,13,0.9)] border border-[rgba(255,198,64,0.2)] hover:border-[rgba(255,198,64,0.5)] hover:shadow-[0_0_30px_rgba(255,198,64,0.15)]'
          }`}
        style={{ backdropFilter: 'blur(8px)' }}
        onClick={() => onSelect(raffle.raffle_id)}
      >
        {/* Featured badge */}
        {isFeatured && (
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-30">
            <span className="px-4 py-1.5 text-[11px] font-heading font-bold bg-gradient-to-r from-[#D4AF37] to-[#FFC640] text-[#090A0D] rounded-full flex items-center gap-1.5 shadow-lg">
              <Crown size={14} /> DESTACADO
            </span>
          </div>
        )}

        {/* Zona del cofre - TRANSPARENTE con overflow visible */}
        <div className="relative h-[100px] sm:h-[120px] flex justify-center items-end overflow-visible">
          {/* Glow radial sutil detrás del cofre */}
          <div className="chest-glow-bg absolute -top-16 left-1/2 -translate-x-1/2 w-[350px] h-[250px] bg-[radial-gradient(ellipse_at_center,rgba(255,198,64,0.3)_0%,transparent_70%)] pointer-events-none" />
          
          {/* Cofre grande - sobresale hacia arriba */}
          <img 
            src="/images/chest-gold.png" 
            alt="Cofre del tesoro"
            className="chest-premium absolute -top-[80px] sm:-top-[100px] lg:-top-[120px] w-[280px] h-[220px] sm:w-[340px] sm:h-[260px] lg:w-[420px] lg:h-[320px] object-contain z-20"
          />
        </div>

        {/* Contenido centrado */}
        <div className="relative z-10 px-6 pb-6 pt-8 text-center">
          {/* Badge precio centrado */}
          <div className="flex justify-center items-center gap-3 mb-3">
            <span className={`px-4 py-1.5 text-sm font-heading font-bold rounded-full
              ${isFree ? 'bg-[rgba(53,208,127,0.2)] text-[#35D07F] border border-[rgba(53,208,127,0.4)]'
                : is100 ? 'bg-[rgba(75,163,255,0.2)] text-[#4BA3FF] border border-[rgba(75,163,255,0.4)]'
                : 'bg-[rgba(255,198,64,0.2)] text-[#FFC640] border border-[rgba(255,198,64,0.4)]'}`}>
              {price}
            </span>
            {isFree && (
              <span className="text-[11px] text-[#35D07F]/80 flex items-center gap-1">
                <Gift size={14} /> 1 cofre gratis
              </span>
            )}
          </div>

          {/* Nombre del sorteo centrado */}
          <h3 className="font-heading font-bold text-xl sm:text-2xl text-[#F7F2E7] mb-2 group-hover:text-[#FFC640] transition-colors">
            {raffle.name}
          </h3>

          {/* Premio principal centrado */}
          <div className="flex items-center justify-center gap-2 mb-4">
            <Trophy size={22} className="text-[#FFC640]" />
            <span className="font-heading font-bold text-2xl sm:text-3xl text-[#FFC640] tabular-nums">
              ${prizeAmount.toLocaleString()}
            </span>
          </div>

          {/* Barra de progreso dinámica */}
          <div className="mb-5">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-[#F7F2E7]/60">
                Vendidos: <span className="text-[#FFC640] font-bold">{sold.toLocaleString()}</span> / {total.toLocaleString()}
              </span>
              <span className="text-[#FFC640] font-heading font-bold tabular-nums text-lg">{percentage}%</span>
            </div>
            <div className="w-full h-3 bg-[rgba(0,0,0,0.5)] rounded-full overflow-hidden border border-white/[0.1]">
              <div 
                className="h-full bg-gradient-to-r from-[#D4AF37] via-[#FFC640] to-[#FFD866] rounded-full transition-all duration-1000 relative"
                style={{ width: `${Math.max(percentage, 1)}%` }}
              />
            </div>
          </div>

          {/* Botón PARTICIPAR centrado */}
          <Button 
            className={`w-full h-14 font-heading font-bold text-lg rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.5)] transition-all duration-300 
              group-hover:shadow-[0_12px_40px_rgba(255,198,64,0.3)] group-hover:scale-[1.02]
              ${isFree ? 'bg-gradient-to-r from-[#2cb86e] to-[#35D07F] text-white hover:from-[#35D07F] hover:to-[#3de088]'
                : is100 ? 'bg-gradient-to-r from-[#3b93ef] to-[#4BA3FF] text-white hover:from-[#4BA3FF] hover:to-[#5bb3ff]'
                : 'bg-gradient-to-r from-[#D4AF37] to-[#FFC640] text-[#090A0D] hover:from-[#FFC640] hover:to-[#FFD866]'}`}
          >
            PARTICIPAR <ChevronRight size={20} className="ml-2" />
          </Button>
        </div>
      </div>
    </>
  );
};

const HistoryCard = ({ raffle }) => {
  return (
    <div className="bg-[#121521] rounded-xl border border-white/[0.06] p-4 flex items-center justify-between gap-4">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <h4 className="font-heading font-semibold text-[#F7F2E7] text-sm truncate">{raffle.name}</h4>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-[rgba(255,198,64,0.1)] text-[#FFC640] font-heading whitespace-nowrap">COMPLETADO</span>
        </div>
        <div className="flex items-center gap-3 text-xs text-[#F7F2E7]/40">
          {raffle.completed_at && <span>{new Date(raffle.completed_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
          {raffle.winner_chest_number !== null && raffle.winner_chest_number !== undefined && (
            <span className="text-[#FFC640]">Cofre ganador: #{String(raffle.winner_chest_number).padStart(4, '0')}</span>
          )}
          {raffle.winner_name && <span>Ganador: {raffle.winner_name}</span>}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className="font-heading font-bold text-[#FFC640] tabular-nums text-sm">${(raffle.template_type === 'free' ? 8000 : (raffle.prize_main_amount || 30000)).toLocaleString()}</span>
      </div>
    </div>
  );
};

const Sorteos = () => {
  const { raffles, user } = useApp();
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await getRaffleHistory();
        setHistory(res.data.raffles || []);
      } catch { }
      setLoadingHistory(false);
    };
    fetch();
  }, []);

  const handleSelect = (raffleId) => {
    if (!user) {
      navigate('/registro');
      return;
    }
    navigate(`/sorteo/${raffleId}/cofres`);
  };

  // Sort: featured first
  const sortedRaffles = [...raffles].sort((a, b) => {
    if (a.featured && !b.featured) return -1;
    if (!a.featured && b.featured) return 1;
    return 0;
  });

  return (
    <div className="relative z-10">
      {/* Active raffles */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="text-center mb-8">
          {/* Contenedor con backdrop blur para mejor legibilidad */}
          <div className="inline-block px-6 py-4 rounded-2xl bg-[rgba(0,0,0,0.35)] backdrop-blur-md border border-white/[0.06]">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[rgba(255,198,64,0.12)] border border-[rgba(255,198,64,0.25)] mb-4">
              <Sparkles size={14} className="text-[#FFC640]" />
              <span className="text-xs text-[#FFC640] font-medium">Sorteos activos</span>
            </div>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl text-[#F7F2E7]">
              Tus Sorteos Disponibles
            </h2>
            <p className="text-sm text-[#F7F2E7]/50 mt-2">Elige un sorteo y empieza a abrir cofres</p>
          </div>
        </div>

        {sortedRaffles.length === 0 ? (
          <div className="text-center py-16">
            <Clock size={40} className="text-[#F7F2E7]/20 mx-auto mb-3" />
            <p className="text-[#F7F2E7]/50">No hay sorteos activos en este momento</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sortedRaffles.map((r) => (
              <RaffleCard key={r.raffle_id} raffle={r} onSelect={handleSelect} />
            ))}
          </div>
        )}
      </section>

      {/* History */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="gold-divider mb-8" />
        <h3 className="font-heading font-bold text-xl text-[#F7F2E7] mb-5 flex items-center gap-2">
          <Clock size={18} className="text-[#F7F2E7]/40" />
          Historial de Sorteos
        </h3>
        {loadingHistory ? (
          <div className="flex justify-center py-8"><Loader2 size={24} className="animate-spin text-[#FFC640]" /></div>
        ) : history.length === 0 ? (
          <p className="text-sm text-[#F7F2E7]/40 py-6 text-center">Aún no hay sorteos completados</p>
        ) : (
          <div className="space-y-3">
            {history.map((r) => <HistoryCard key={r.raffle_id} raffle={r} />)}
          </div>
        )}
      </section>
    </div>
  );
};

export default Sorteos;
